import { type ThemeOptions } from '@mui/material/styles';
import { type ColorSchemeExtended } from './schemes';

type CreateThemeProps = {
  colorScheme: ColorSchemeExtended;
  lightestColor: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  primaryFont: string;
  secondaryFont: string;
};

export const createThemeOptions = ({
  colorScheme,
  lightestColor,
  primaryColor,
  secondaryColor,
  tertiaryColor,
  primaryFont,
  secondaryFont,
}: CreateThemeProps): ThemeOptions => ({
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 1024,
      lg: 1254,
    },
  },
  palette: {
    primary: {
      main: primaryColor,
      lightest: lightestColor,
    },
    secondary: {
      main: secondaryColor,
      ...colorScheme,
    },
    tertiary: {
      main: tertiaryColor,
    },
    text: { primary: colorScheme.foreground },
    background: {
      default: colorScheme.background,
      paper: colorScheme.background,
    },
    error: {
      main: '#d80034',
    },
    success: {
      main: '#20c26e',
    },
    warning: {
      main: '#ff6624',
    },
  },
  typography: {
    /** Not cookbook standard - taken from designs */
    body1xs: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
    },
    /** Not cookbook standard - taken from designs */
    body1: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '20px',
      fontWeight: 400,
      lineHeight: '28px',
    },
    body1Medium: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px',
    },
    body1Bold: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
    },
    body2: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: '18px',
    },
    body2Medium: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: '18px',
    },
    body2Bold: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '13px',
      fontWeight: 700,
      lineHeight: '18px',
    },
    button: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '24px',
    },
    /** Not cookbook standard - taken from designs */
    buttonSmall: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '18px',
    },
    caption: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
    },
    /** Not cookbook standard - taken from designs */
    finePrint1: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
    },
    /** Not cookbook standard - taken from designs */
    finePrint1Medium: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
    },
    /** Not cookbook standard - taken from designs */
    finePrint2: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '10px',
      fontWeight: 400,
      lineHeight: '12px',
    },
    /** Not cookbook standard - taken from designs */
    finePrint2Medium: {
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontSize: '10px',
      fontWeight: 500,
      lineHeight: '12px',
    },
    /** Not cookbook standard - taken from designs */
    finePrint3: {
      fontFamily: `${secondaryFont},Roboto,sans-serif`,
      fontSize: '8px',
      fontWeight: 400,
      lineHeight: '10px',
    },
    /** Not cookbook standard - taken from designs */
    h1: {
      fontSize: '48px',
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontWeight: 600,
      lineHeight: '64px',
    },
    h1DisplayXs: {
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontSize: '36px',
      fontWeight: 600,
      lineHeight: '40px',
    },
    h1xs: {
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontSize: '28px',
      fontWeight: 500,
      lineHeight: '36px',
    },
    h2: {
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontSize: '24px',
      fontWeight: 500,
      lineHeight: '32px',
    },
    /** Not cookbook standard - taken from designs */
    h2Display: {
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontSize: '36px',
      fontWeight: 600,
      lineHeight: '40px',
    },
    h2xs: {
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontSize: '21px',
      fontWeight: 500,
      lineHeight: '28px',
    },
    h3: {
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: '24px',
    },
    /** Not cookbook standard - taken from designs */
    h3xs: {
      fontFamily: `${primaryFont},Roboto,sans-serif`,
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '21px',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1xs: 'h1',
          h1DisplayXs: 'h1',
          h2xs: 'h2',
          h2Display: 'h2',
          h3xs: 'h3',
        },
      },
    },
  },
});
